import { useMemo, useState } from "preact/hooks"
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"
import { Button } from "../../../components/ui/button"
import { StatCard } from "../../../components/StatCard"
import { day_label, fmt_money, fmt_qty, num } from "./format"
import type { ProjectionTotal, ProjectionView } from "./types"

type Metric = "units" | "cases" | "dollars"

const METRICS: { value: Metric; label: string }[] = [
  { value: "dollars", label: "Dollars" },
  { value: "units", label: "Units" },
  { value: "cases", label: "Cases" },
]

function fmt_metric(metric: Metric, value: number | string): string {
  return metric === "dollars" ? fmt_money(value) : fmt_qty(value)
}

/** Each item × week, for the pivot below the chart. */
function pivot_by_item(data: ProjectionView, metric: Metric): { label: string; upc: string; weeks: Record<string, number>; total: number }[] {
  const items = new Map<string, { label: string; upc: string; weeks: Record<string, number>; total: number }>()
  for (const row of data.rows) {
    const value = num(metric === "units" ? row.projected_units : metric === "cases" ? row.projected_cases : row.projected_dollars)
    const item = items.get(row.upc) ?? { label: row.item_name, upc: row.upc, weeks: {}, total: 0 }
    item.weeks[row.week_start] = (item.weeks[row.week_start] ?? 0) + value
    item.total += value
    items.set(row.upc, item)
  }
  return [...items.values()].sort((a, b) => b.total - a.total)
}

export function ProjectionsTab({ data, className }: { data: ProjectionView; className?: string }) {
  const [metric, set_metric] = useState<Metric>("dollars")
  const weeks = data.weeks.map((w: ProjectionTotal) => ({ week: day_label(w.key), key: w.key, value: num(w[metric]) }))
  const pivot = useMemo(() => pivot_by_item(data, metric), [data, metric])
  if (!data.as_of_date) {
    return <p class="text-sm text-gray-600">No KeHE order projections pulled yet. They arrive with the daily KeHE reports sync.</p>
  }
  return (
    <div class={`space-y-4 ${className ?? ""}`}>
      <div class="grid gap-4 md:grid-cols-3">
        <StatCard title="Projected orders" value={fmt_money(data.dollars)} detail={`over the next ${data.weeks.length} weeks`} />
        <StatCard title="Units" value={fmt_qty(data.units)} detail={`${fmt_qty(data.cases)} cases`} />
        <StatCard title="Biggest DC" value={data.dcs[0]?.label ?? "—"} detail={data.dcs[0] ? fmt_money(data.dcs[0].dollars) : ""} />
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base">What KeHE expects to order from you, by week</CardTitle>
          <div class="flex gap-1">
            {METRICS.map((m: { value: Metric; label: string }) => (
              <Button key={m.value} size="sm" variant={metric === m.value ? "default" : "outline"} onClick={() => set_metric(m.value)}>
                {m.label}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={weeks}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" fontSize={12} />
              <YAxis fontSize={12} width={80} tickFormatter={(v: number) => fmt_metric(metric, v)} />
              <Tooltip formatter={(v: number) => fmt_metric(metric, v)} />
              <Bar dataKey="value" name={METRICS.find((m) => m.value === metric)?.label} fill="#2563eb" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">By item and week</CardTitle>
        </CardHeader>
        <CardContent className="max-h-[520px] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                {data.weeks.map((w: ProjectionTotal) => (
                  <TableHead key={w.key} className="text-right whitespace-nowrap">
                    {day_label(w.key)}
                  </TableHead>
                ))}
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pivot.map((item: { label: string; upc: string; weeks: Record<string, number>; total: number }) => (
                <TableRow key={item.upc}>
                  <TableCell className="whitespace-nowrap">{item.label}</TableCell>
                  {data.weeks.map((w: ProjectionTotal) => (
                    <TableCell key={w.key} className="text-right">
                      {item.weeks[w.key] ? fmt_metric(metric, item.weeks[w.key]) : "—"}
                    </TableCell>
                  ))}
                  <TableCell className="text-right font-medium">{fmt_metric(metric, item.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
